import { useTerminology } from '@/lib/useTerminology'
/**
 * AP-13: Edit/New-Seite für TimeProjectStepRef.
 *
 * Routen:
 *   /time-tracking/steps/new
 *   /time-tracking/steps/:id/edit
 *
 * Kaskade: Projekt → Position → Schritt (Project wird aus Position abgeleitet).
 *
 * AP-36 Phase E: Im Bearbeiten-Modus speichert jedes Feld beim Verlassen — ohne
 * Knopf. Der Anlegen-Modus bleibt ein Formular mit lokalem Zustand und dem Knopf
 * „Anlegen". Projekt und Position prüft der Server nur zusammen; sie bilden
 * deshalb eine Feldgruppe.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/axios';
import {
  FeldStatus,
  Feldgruppe,
  feldKlasse,
  mitStand,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import {
  createStepRef,
  deleteStepRef,
  getStepRef,
  getRefSchemas,
  listPositionRefs,
  listProjectRefs,
} from '../api/time-master-data.api';
import { CustomFieldsForm, validateRequired } from '../components/CustomFieldsForm';
import { ModeBanner } from '../components/ModeBanner';
import { useDataSourceMode } from '../hooks/useDataSourceMode';
import {
  fieldLabel,
  fieldWrapper,
  formCard,
  inputBase,
  pageTitle,
  selectBase,
  textareaBase,
} from '@/lib/form-styles';
import type {
  CustomFieldsSchema,
  TimeProjectPositionRef,
  TimeProjectRef,
  TimeProjectStepRef,
} from '../types/time-master-data.types';
import { bestaetige } from '@/lib/dialoge';

interface FormState {
  projectRefId: string;
  positionRefId: string;
  nummer: string;
  name: string;
  description: string;
  kategoriNummer: string;
  kategorieTxt: string;
  isActive: boolean;
  customFields: Record<string, unknown>;
}

const EMPTY: FormState = {
  projectRefId: '',
  positionRefId: '',
  nummer: '',
  name: '',
  description: '',
  kategoriNummer: '',
  kategorieTxt: '',
  isActive: true,
  customFields: {},
};

const ladeText = <div className="p-6 text-sm text-muted-foreground">Wird geladen …</div>;

/** Pflichtfelder der Zusatzfelder — dieselbe Prüfung wie früher vor dem Speichern. */
function pflichtfelderText(schema: CustomFieldsSchema | null, werte: Record<string, unknown>): string | null {
  const fehlend = Object.keys(validateRequired(schema, werte));
  return fehlend.length === 0 ? null : `Pflichtfeld fehlt: ${fehlend.join(', ')}`;
}

function positionsText(p: TimeProjectPositionRef): string {
  return `${p.nummer != null ? `${p.nummer} – ` : ''}${p.name}`;
}

export default function TimeProjectStepRefFormPage() {
  const { id } = useParams<{ id?: string }>();
  return id ? <BearbeitenSeite id={id} /> : <AnlegenSeite />;
}

// ── Bearbeiten: feldweise, ohne Knopf ───────────────────────────────────────

function BearbeitenSeite({ id }: { id: string }) {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const navigate = useNavigate();
  const { canEdit, isLoading: modeLoading } = useDataSourceMode('step');

  const [row, setRow] = useState<TimeProjectStepRef | null>(null);
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);
  const [projects, setProjects] = useState<TimeProjectRef[]>([]);
  const [positions, setPositions] = useState<TimeProjectPositionRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [schemas, projList, geladen] = await Promise.all([
          getRefSchemas(),
          listProjectRefs({ page: 1, limit: 500 }),
          getStepRef(id),
        ]);
        if (cancelled) return;
        setSchema(schemas.step);
        setProjects(projList.data);
        setRow(geladen);
        const posList = await listPositionRefs({ projectRefId: geladen.projectRefId, page: 1, limit: 500 });
        if (!cancelled) setPositions(posList.data);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `timeStepRef:${id}`,
    bezeichnung: row ? `Arbeitsschritt ${row.name}` : 'Arbeitsschritt',
    daten: row ? (row as unknown as Datensatz) : null,
    schreibe: (patch, stand) =>
      api.patch<Datensatz>(`/time-master-data/steps/${id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/time-master-data/steps/${id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  const gesperrt = !canEdit;
  const nummer          = useFeld<number | null>(stelle, 'nummer', { label: 'Nummer', art: 'zahl', stellen: 0, gesperrt });
  const bezeichnung     = useFeld<string | null>(stelle, 'name', { label: 'Bezeichnung', pflicht: true, gesperrt });
  const beschreibung    = useFeld<string | null>(stelle, 'description', { label: 'Beschreibung', gesperrt });
  const kategorieNummer = useFeld<number | null>(stelle, 'kategoriNummer', { label: 'Kategorie-Nummer', art: 'zahl', stellen: 0, gesperrt });
  const kategorieText   = useFeld<string | null>(stelle, 'kategorieTxt', { label: 'Kategorie-Text', gesperrt });
  const aktiv           = useFeld<boolean>(stelle, 'isActive', { label: 'aktiv', art: 'schalter', gesperrt });

  // Die Zusatzfelder sind ein Eintrag: bearbeitet wird ein Entwurf, gespeichert
  // wird er beim Verlassen der Hülle (der Abschnitt darunter).
  const daten = useStellenDaten(stelle);
  const gespeicherteZusatzfelder = useMemo(
    () => (daten.customFields as Record<string, unknown> | null) ?? {},
    [daten.customFields],
  );
  const zusatzfelder = useEntwurf<Record<string, unknown>>(stelle, {
    kennung: 'customFields',
    gespeichert: gespeicherteZusatzfelder,
    felder: ['customFields'],
    label: 'Zusatzfelder',
    gesperrt,
    operation: (entwurf) => () => ({ customFields: entwurf }),
    pruefe: (entwurf) => pflichtfelderText(schema, entwurf),
  });

  /** Der Server nimmt Position und Projekt nur zusammen an — deshalb die Gruppe. */
  const pruefeZuordnung = (werte: Datensatz): string | null => {
    const position = positions.find((p) => p.id === werte.positionRefId);
    if (!position || !werte.projectRefId) return null;
    return position.projectRefId === werte.projectRefId
      ? null
      : `Diese ${terms.position} gehört zu ${terms.project === 'Auftrag' ? 'einem anderen Auftrag' : 'einem anderen Projekt'}.`;
  };

  const deaktivieren = async () => {
    if (!(await bestaetige('Arbeitsschritt wirklich deaktivieren? Er bleibt erhalten und kann später wieder aktiviert werden.'))) return;
    setError(null);
    try { await deleteStepRef(id); navigate('/time-tracking/steps'); }
    catch (e) { setError((e as Error).message); }
  };

  if (loading || modeLoading) return ladeText;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <header className="flex items-center justify-between">
        <h1 className={pageTitle}>Arbeitsschritt bearbeiten</h1>
        <button type="button" onClick={() => navigate('/time-tracking/steps')} className="text-sm text-muted-foreground hover:underline">
          ← Zurück zur Liste
        </button>
      </header>

      <ModeBanner kind="step" />

      {error && <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className={formCard + ' space-y-4'}>
        <Feldgruppe stelle={stelle} label={`${terms.project} / ${terms.position}`} pruefe={pruefeZuordnung}>
          <div className="grid grid-cols-2 gap-4">
            <ProjektFeld stelle={stelle} label={terms.project} projects={projects} />
            <PositionFeld stelle={stelle} label={terms.position} positions={positions} gesperrt={gesperrt} />
          </div>
        </Feldgruppe>

        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Nummer (optional)</label>
            <input inputMode="numeric" className={feldKlasse} {...nummer.props} />
            <FeldStatus status={nummer} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Bezeichnung *</label>
            <input type="text" className={feldKlasse} {...bezeichnung.props} />
            <FeldStatus status={bezeichnung} />
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>Beschreibung</label>
          <textarea className={feldKlasse} rows={3} {...beschreibung.props} />
          <FeldStatus status={beschreibung} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Kategorie-Nummer</label>
            <input inputMode="numeric" className={feldKlasse} {...kategorieNummer.props} />
            <FeldStatus status={kategorieNummer} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Kategorie-Text</label>
            <input type="text" className={feldKlasse} {...kategorieText.props} />
            <FeldStatus status={kategorieText} />
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 select-none">
            <input type="checkbox" className="h-4 w-4" {...aktiv.schalter} />
            <span className="text-sm">aktiv</span>
          </label>
          <FeldStatus status={aktiv} />
        </div>
      </section>

      {schema && schema.fields.length > 0 && (
        <section className={formCard + ' space-y-3'} {...zusatzfelder.huelle}>
          <h2 className="text-base font-semibold text-primary">Zusatzfelder</h2>
          <CustomFieldsForm schema={schema} value={zusatzfelder.entwurf ?? {}}
            onChange={(v) => zusatzfelder.setze(v)}
            disabled={gesperrt} />
          <FeldStatus status={zusatzfelder} />
        </section>
      )}

      <div>
        <button type="button" onClick={deaktivieren} disabled={gesperrt}
          className="px-3 py-2 text-sm rounded-md border border-red-400 text-red-700 hover:bg-red-50 disabled:opacity-50">
          Deaktivieren
        </button>
      </div>
    </div>
  );
}

/**
 * Das Projekt bleibt nach dem Anlegen unverändert — es ist gesperrt und steht
 * deshalb wie Text da (AP-36 4.1). Es gehört trotzdem in die Gruppe: Die
 * Prüfung der Position liest es mit.
 */
function ProjektFeld({ stelle, label, projects }: { stelle: Speicherstelle<unknown>; label: string; projects: TimeProjectRef[] }) {
  const feld = useFeld<string | null>(stelle, 'projectRefId', { label, art: 'auswahl', gesperrt: true });
  return (
    <div className={fieldWrapper}>
      <label className={fieldLabel}>{label}</label>
      <select className={feldKlasse} {...feld.props}>
        <option value="">— bitte wählen —</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.projectNumber} – {p.name}</option>)}
      </select>
      <FeldStatus status={feld} />
    </div>
  );
}

function PositionFeld({ stelle, label, positions, gesperrt }: { stelle: Speicherstelle<unknown>; label: string; positions: TimeProjectPositionRef[]; gesperrt: boolean }) {
  const feld = useFeld<string | null>(stelle, 'positionRefId', { label, art: 'auswahl', pflicht: true, gesperrt });
  return (
    <div className={fieldWrapper}>
      <label className={fieldLabel}>{label} *</label>
      <select className={feldKlasse} {...feld.props}>
        <option value="">— bitte wählen —</option>
        {positions.map((p) => <option key={p.id} value={p.id}>{positionsText(p)}</option>)}
      </select>
      <FeldStatus status={feld} />
    </div>
  );
}

// ── Anlegen: Formular mit Knopf ─────────────────────────────────────────────

function AnlegenSeite() {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { canEdit, isLoading: modeLoading } = useDataSourceMode('step');

  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    projectRefId:  search.get('projectRefId')  ?? '',
    positionRefId: search.get('positionRefId') ?? '',
  }));
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);
  const [projects, setProjects] = useState<TimeProjectRef[]>([]);
  const [positions, setPositions] = useState<TimeProjectPositionRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cfErrors, setCfErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [schemas, projList] = await Promise.all([
          getRefSchemas(),
          listProjectRefs({ page: 1, limit: 500 }),
        ]);
        if (cancelled) return;
        setSchema(schemas.step);
        setProjects(projList.data);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Positions laden, wenn Projekt wechselt
  useEffect(() => {
    if (!form.projectRefId) { setPositions([]); return; }
    let cancelled = false;
    listPositionRefs({ projectRefId: form.projectRefId, page: 1, limit: 500 })
      .then((res) => { if (!cancelled) setPositions(res.data); })
      .catch(() => { if (!cancelled) setPositions([]); });
    return () => { cancelled = true; };
  }, [form.projectRefId]);

  const disabled = !canEdit || saving;

  const submit = async () => {
    setError(null);
    if (!form.projectRefId)  { setError('Projekt ist Pflicht.');        return; }
    if (!form.positionRefId) { setError(`${terms.position} ist Pflicht.`); return; }
    if (!form.name.trim())   { setError('Bezeichnung ist Pflicht.');    return; }
    const reqErrors = validateRequired(schema, form.customFields);
    if (Object.keys(reqErrors).length > 0) {
      setCfErrors(reqErrors);
      setError('Bitte Pflichtfelder ausfüllen.');
      return;
    }
    setCfErrors({});
    setSaving(true);
    try {
      await createStepRef({
        projectRefId:  form.projectRefId,
        positionRefId: form.positionRefId,
        nummer:        form.nummer === '' ? null : Number(form.nummer),
        name:          form.name.trim(),
        description:   form.description.trim() || null,
        kategoriNummer: form.kategoriNummer === '' ? null : Number(form.kategoriNummer),
        kategorieTxt:  form.kategorieTxt.trim() || null,
        isActive:      form.isActive,
        customFields:  form.customFields,
      });
      navigate('/time-tracking/steps');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || modeLoading) return ladeText;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <header className="flex items-center justify-between">
        <h1 className={pageTitle}>Neuer Arbeitsschritt</h1>
        <button type="button" onClick={() => navigate('/time-tracking/steps')} className="text-sm text-muted-foreground hover:underline">
          ← Zurück zur Liste
        </button>
      </header>

      <ModeBanner kind="step" />

      {error && <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className={formCard + ' space-y-4'}>
        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>{terms.project} *</label>
            <select className={selectBase}
              value={form.projectRefId}
              onChange={(e) => setForm((f) => ({ ...f, projectRefId: e.target.value, positionRefId: '' }))}
              disabled={disabled}>
              <option value="">— bitte wählen —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.projectNumber} – {p.name}</option>)}
            </select>
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>{terms.position} *</label>
            <select className={selectBase}
              value={form.positionRefId}
              onChange={(e) => setForm((f) => ({ ...f, positionRefId: e.target.value }))}
              disabled={disabled || !form.projectRefId}>
              <option value="">— bitte wählen —</option>
              {positions.map((p) => <option key={p.id} value={p.id}>{positionsText(p)}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Nummer (optional)</label>
            <input type="number" className={inputBase}
              value={form.nummer}
              onChange={(e) => setForm((f) => ({ ...f, nummer: e.target.value }))}
              disabled={disabled} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Bezeichnung *</label>
            <input type="text" className={inputBase}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              disabled={disabled} />
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>Beschreibung</label>
          <textarea className={textareaBase} rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            disabled={disabled} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Kategorie-Nummer</label>
            <input type="number" className={inputBase}
              value={form.kategoriNummer}
              onChange={(e) => setForm((f) => ({ ...f, kategoriNummer: e.target.value }))}
              disabled={disabled} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Kategorie-Text</label>
            <input type="text" className={inputBase}
              value={form.kategorieTxt}
              onChange={(e) => setForm((f) => ({ ...f, kategorieTxt: e.target.value }))}
              disabled={disabled} />
          </div>
        </div>

        <label className="flex items-center gap-2 select-none">
          <input type="checkbox" checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            disabled={disabled} className="h-4 w-4" />
          <span className="text-sm">aktiv</span>
        </label>
      </section>

      {schema && schema.fields.length > 0 && (
        <section className={formCard + ' space-y-3'}>
          <h2 className="text-base font-semibold text-primary">Zusatzfelder</h2>
          <CustomFieldsForm schema={schema} value={form.customFields}
            onChange={(v) => setForm((f) => ({ ...f, customFields: v }))}
            disabled={disabled} />
          {Object.keys(cfErrors).length > 0 && (
            <ul className="text-xs text-red-600 list-disc list-inside">
              {Object.entries(cfErrors).map(([k, v]) => <li key={k}>{k}: {v}</li>)}
            </ul>
          )}
        </section>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => navigate('/time-tracking/steps')}
          className="px-3 py-2 text-sm rounded-md border" disabled={saving}>
          Abbrechen
        </button>
        <button type="button" onClick={submit} disabled={disabled}
          className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
          {saving ? 'Legt an …' : 'Anlegen'}
        </button>
      </div>
    </div>
  );
}
